import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Icon,
  LoadingBlock,
  PageHeader,
  StatusView,
  useToast,
} from '@/components/ui';
import { SampleBrowser } from '@/features/eeg/components/SampleBrowser';
import { WaveformCanvas } from '@/features/eeg/components/WaveformCanvas';
import { useRawEeg } from '@/features/eeg/useRawEeg';
import { copyToClipboard, saveBinary, saveText } from '@/lib/download';
import {
  formatBytes,
  formatCount,
  formatDuration,
  formatMode,
  formatSessionDateTime,
} from '@/lib/formatting';
import { TimelineExportCard, TimelineExportItem } from '../components/TimelineExport';
import { useSession, useTimelineFileStatus } from '../hooks';
import {
  effectiveDurationSec,
  sessionIdentifier,
  sessionTimestamp,
  titleFor,
  type SessionSummary,
} from '../model';

import styles from './SessionDetailPage.module.css';

export function SessionDetailPage() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const { session, isPending, isError, refetch } = useSession(sessionId);
  const locale = navigator.language.split('-')[0] ?? 'en';

  if (isPending) {
    return (
      <>
          <PageHeader back={{ to: '/sessions', label: 'All sessions' }} title="Session" />
          <Card>
            <LoadingBlock label="Loading session…" />
          </Card>
      </>
    );
  }

  if (isError) {
    return (
      <>
          <PageHeader back={{ to: '/sessions', label: 'All sessions' }} title="Session" />
          <Card>
            <StatusView
              icon="cloud-off"
              tone="danger"
              title="Could not load this session"
              message="The session list failed to load, so this record could not be found."
              action={
                <Button variant="primary" icon="refresh" onClick={() => void refetch()}>
                  Retry
                </Button>
              }
            />
          </Card>
      </>
    );
  }

  if (session === null) {
    return (
      <>
          <PageHeader back={{ to: '/sessions', label: 'All sessions' }} title="Session not found" />
          <Card>
            <StatusView
              icon="alert"
              tone="warning"
              title="Session not found"
              message="No session with this identifier belongs to your account. It may have been removed, or the link may be out of date."
            />
          </Card>
      </>
    );
  }

  return <SessionDetailView session={session} locale={locale} />;
}

function SessionDetailView({ session, locale }: { session: SessionSummary; locale: string }) {
  const raw = useRawEeg(session);
  const timeline = useTimelineFileStatus(session);
  const toast = useToast();

  const duration = effectiveDurationSec(session);
  const title = titleFor(session, locale);
  const englishTitle = session.titles.en?.trim();

  return (
    <>
      <PageHeader
        back={{ to: '/sessions', label: 'All sessions' }}
        eyebrow={
          <>
            <Icon name="list" size={13} />
            Session detail
          </>
        }
        title={title}
        description={formatSessionDateTime(sessionTimestamp(session))}
        actions={
          <div className={styles.badges}>
            {session.mode !== null ? <Badge tone="accent">{formatMode(session.mode)}</Badge> : null}
            {raw.status === 'loaded' ? (
              <Badge tone="signal" dot>
                Raw EEG
              </Badge>
            ) : null}
            {timeline === 'exists' ? (
              <Badge tone="signal" dot>
                Timeline
              </Badge>
            ) : timeline === 'checking' ? (
              <Badge>Timeline</Badge>
            ) : null}
          </div>
        }
      />

      <div className={styles.layout}>
        <div className={styles.column}>
          <RawEegCard session={session} raw={raw} toast={toast} />
        </div>

        <div className={styles.column}>
          <Card>
            <CardHeader title="Session record" subtitle="As stored in session_summaries" />
            <CardBody>
              <div className={styles.metaGrid}>
                <Meta label="Mode" value={session.mode === null ? 'Unknown' : formatMode(session.mode)} />
                <Meta label="Duration" value={formatDuration(duration)} />
                <Meta label="Date" value={formatSessionDateTime(sessionTimestamp(session))} />
                {session.valuesAvg !== null ? (
                  <Meta label="Average" value={session.valuesAvg.toFixed(3)} mono />
                ) : null}
                {session.valuesMin !== null ? (
                  <Meta label="Minimum" value={session.valuesMin.toFixed(3)} mono />
                ) : null}
                {session.valuesMax !== null ? (
                  <Meta label="Maximum" value={session.valuesMax.toFixed(3)} mono />
                ) : null}
              </div>
            </CardBody>
          </Card>

          <Card>
            <CardHeader title="Identifiers" />
            <CardBody>
              <div className={styles.metaGrid}>
                <Meta label="Session ID" value={sessionIdentifier(session)} mono />
                <Meta label="Record ID" value={session.id} mono />
                {session.contentId !== null ? (
                  <Meta label="Content ID" value={session.contentId} mono />
                ) : null}
                {session.rawFileId !== null ? (
                  <Meta label="Raw file" value={session.rawFileId} mono />
                ) : null}
                {session.timelineFileId !== null ? (
                  <Meta label="Timeline file" value={session.timelineFileId} mono />
                ) : null}
              </div>
            </CardBody>
          </Card>

          {englishTitle ? (
            <Card>
              <CardHeader title="Localised titles" />
              <CardBody>
                <div className={styles.metaGrid}>
                  <div className={styles.metaItem}>
                    <div className={styles.metaValue}>{englishTitle}</div>
                  </div>
                </div>
              </CardBody>
            </Card>
          ) : null}
        </div>
      </div>
    </>
  );
}

function Meta({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className={styles.metaItem}>
      <div className={styles.metaLabel}>{label}</div>
      <div className={mono ? styles.metaMono : styles.metaValue}>{value}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------

type RawState = ReturnType<typeof useRawEeg>;

function RawEegCard({
  session,
  raw,
  toast,
}: {
  session: SessionSummary;
  raw: RawState;
  toast: ReturnType<typeof useToast>;
}) {
  const [window, setWindow] = useState<{ start: number; end: number }>({ start: 0, end: 0 });

  const data = raw.status === 'loaded' ? raw.data : null;

  // Reset the viewport whenever a different recording loads.
  useEffect(() => {
    if (data) setWindow({ start: 0, end: data.sampleCount });
  }, [data]);

  const baseName = useMemo(
    () => (data ? data.fileName : `raw_data_${session.id}.raw.bin`),
    [data, session.id],
  );

  if (raw.status === 'initial' || raw.status === 'loading') {
    return (
      <Card>
        <CardHeader title="Raw EEG data" />
        <LoadingBlock
          label={raw.phase === 'decoding' ? 'Decoding samples…' : 'Downloading recording…'}
        />
      </Card>
    );
  }

  if (raw.status === 'unavailable') {
    return (
      <>
        <Card>
          <CardHeader title="Raw EEG data" />
          <StatusView
            icon="file"
            compact
            title="No raw recording available"
            message={raw.message ?? 'No raw data file for this session.'}
          />
        </Card>
        <TimelineExportCard session={session} />
      </>
    );
  }

  if (raw.status === 'failure' || data === null) {
    return (
      <>
        <Card>
          <CardHeader title="Raw EEG data" />
          <StatusView
            icon="cloud-off"
            tone="danger"
            compact
            title="Could not load the recording"
            message={raw.message ?? 'Could not load the raw data file.'}
            action={
              <Button variant="primary" icon="refresh" onClick={raw.reload}>
                Retry
              </Button>
            }
          />
        </Card>
        <TimelineExportCard session={session} />
      </>
    );
  }

  const onCopy = async () => {
    const copied = await copyToClipboard(data.fullText);
    if (copied) toast.success(`${formatCount(data.sampleCount)} samples copied`);
    else toast.error('Could not copy to the clipboard.');
  };

  const decodedName = `${baseName.replace(/\.bin$|\.raw$|\.gz$/g, '')}_decoded.txt`;
  const csvName = `${baseName.replace(/\.bin$|\.raw$|\.gz$/g, '')}_samples.csv`;

  const onSaveBinary = async () => {
    const outcome = await saveBinary(data.binary, baseName);
    if (outcome !== 'cancelled') toast.success(`${baseName} saved`);
  };

  const onSaveDecoded = async () => {
    const outcome = await saveText(data.fullText, decodedName);
    if (outcome !== 'cancelled') toast.success(`${decodedName} saved`);
  };

  const onSaveCsv = async () => {
    // index,value — the shape a spreadsheet or analysis script expects.
    const lines: string[] = ['index,value'];
    for (let i = 0; i < data.samples.length; i += 1) lines.push(`${i},${data.samples[i]}`);
    const outcome = await saveText(lines.join('\n'), csvName, 'text/csv');
    if (outcome !== 'cancelled') toast.success(`${csvName} saved`);
  };

  const span = window.end - window.start;
  const zoomedIn = span < data.sampleCount;

  return (
    <>
      <Card>
        <CardHeader
          title="Raw EEG recording"
          subtitle="Decoded in your browser from the stored binary payload"
        />

        <div className={styles.fileBar}>
          <span className={styles.fileName}>
            <Icon name="file" size={14} />
            <span className={styles.fileNameText}>{data.fileName}</span>
          </span>
          <span className={styles.fileMeta}>
            {formatBytes(data.byteCount)} · {formatCount(data.sampleCount)} samples
          </span>
          <span className={styles.fileActions}>
            <Button size="sm" icon="copy" onClick={() => void onCopy()}>
              Copy samples
            </Button>
            <Button size="sm" icon="download" onClick={() => void onSaveBinary()}>
              Download binary
            </Button>
          </span>
        </div>

        <div className={styles.signalStats}>
          <SignalStat label="Minimum" value={data.stats.min} />
          <SignalStat label="Maximum" value={data.stats.max} />
          <SignalStat label="Mean" value={data.stats.mean.toFixed(2)} />
          <SignalStat label="RMS" value={data.stats.rms.toFixed(2)} />
          <SignalStat label="Peak-to-peak" value={data.stats.max - data.stats.min} />
        </div>

        <CardBody className={styles.viewerBody}>
          <WaveformCanvas
            samples={data.samples}
            start={window.start}
            end={window.end}
            onWindowChange={(start, end) => setWindow({ start, end })}
            height={260}
          />

          <div className={styles.zoomBar}>
            <Button
              size="sm"
              icon="zoom-out"
              disabled={!zoomedIn}
              onClick={() => setWindow({ start: 0, end: data.sampleCount })}
            >
              Reset zoom
            </Button>
            <span className={styles.zoomSpacer} />
            <span className={styles.window}>
              showing {formatCount(window.start)}–{formatCount(window.end)} of{' '}
              {formatCount(data.sampleCount)}
            </span>
          </div>

          <div className={styles.formatNote}>
            <Icon name="info" size={14} className={styles.formatIcon} />
            <span>
              Samples are decoded as 16-bit signed, big-endian two's-complement integers, two bytes
              per sample — the format the ela headband firmware emits. A trailing partial sample, if
              the last BLE chunk was cut short, is dropped rather than treated as an error.
            </span>
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardHeader title="Decoded samples" subtitle="Ten per row, as in the ela app" />
        <CardBody>
          <SampleBrowser samples={data.samples} />
        </CardBody>
      </Card>

      <Card>
        <CardHeader title="Decoder output" subtitle="The summary the ela app prints" />
        <CardBody>
          <pre className={styles.preview}>{data.preview}</pre>
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          title="Export"
          subtitle="Two distinct formats — keep them apart"
        />
        <CardBody>
          <div className={styles.exportList}>
            <div className={styles.exportItem}>
              <div className={styles.exportTitle}>Binary payload · {baseName}</div>
              <p className={styles.exportDesc}>
                The decompressed <code>.raw.bin</code> exactly as the ela app uploaded it — the
                concatenated BLE chunk payloads, not human-readable. This is the file to hand to
                anything that expects ELA's native format.
              </p>
              <span>
                <Button size="sm" icon="download" onClick={() => void onSaveBinary()}>
                  Download .raw.bin
                </Button>
              </span>
            </div>

            <div className={styles.exportItem}>
              <div className={styles.exportTitle}>Decoded samples · {decodedName}</div>
              <p className={styles.exportDesc}>
                Every decoded sample as comma-separated integers — the same text the Copy button
                puts on your clipboard. Human-readable, and what the ela app's Open action produces.
              </p>
              <span>
                <Button size="sm" icon="download" onClick={() => void onSaveDecoded()}>
                  Download .txt
                </Button>
              </span>
            </div>

            <div className={styles.exportItem}>
              <div className={styles.exportTitle}>Sample table · {csvName}</div>
              <p className={styles.exportDesc}>
                An <code>index,value</code> CSV for a spreadsheet, MNE, pandas or MATLAB.
              </p>
              <span>
                <Button size="sm" icon="download" onClick={() => void onSaveCsv()}>
                  Download .csv
                </Button>
              </span>
            </div>

            <TimelineExportItem session={session} />
          </div>
        </CardBody>
      </Card>
    </>
  );
}

function SignalStat({ label, value }: { label: string; value: number | string }) {
  return (
    <div className={styles.signalStat}>
      <span className={styles.signalLabel}>{label}</span>
      <span className={styles.signalValue}>{value}</span>
    </div>
  );
}
